<template>
  <main class="thanks-tool">
    <section class="thanks-editor">
      <div class="container-fluid tool-container">
        <div class="tool-heading">
          <span>Teşekkür aracı</span>
          <h1>Teşekkür Belgesi Oluştur</h1>
          <p>Bağış, gönüllü destek, organizasyon katkısı ve örnek davranışlar için paylaşılabilir özel teşekkür belgesi hazırlayın.</p>
        </div>

        <div class="tool-layout">
          <form class="tool-form" @submit.prevent="downloadAsPng">
            <div>
              <label for="recipient" class="form-label">Kişi / Kurum Adı</label>
              <input id="recipient" v-model="recipient" type="text" class="form-control" placeholder="Ad Soyad veya kurum adı" />
            </div>

            <div>
              <label for="date" class="form-label">Tarih</label>
              <input id="date" v-model="date" type="text" class="form-control" placeholder="05.06.2026" />
            </div>

            <div>
              <label for="reason" class="form-label">Kısa Katkı Başlığı</label>
              <input id="reason" v-model="reason" type="text" class="form-control" placeholder="Derneğimize yapmış olduğu bağış ve destek" />
            </div>

            <div>
              <label for="message" class="form-label">Teşekkür Metni</label>
              <textarea id="message" v-model="message" class="form-control" rows="7" placeholder="Teşekkür metnini yazın." />
            </div>

            <div>
              <label for="signer" class="form-label">İmza / Yetkili</label>
              <input id="signer" v-model="signer" type="text" class="form-control" placeholder="Yönetim Kurulu" />
            </div>

            <div class="tool-actions">
              <button type="submit" class="btn btn-primary">
                <i class="fa fa-download me-2"></i>PNG Olarak İndir
              </button>
            </div>
          </form>

          <div class="preview-shell">
            <div class="preview-note">
              <i class="fa fa-mobile-screen-button"></i>
              1080 x 1350 portre paylaşım formatı
            </div>

            <div ref="posterFrame" class="poster-scale">
              <div ref="captureArea" class="thanks-poster" :style="{ '--poster-scale': posterScale }">
                <div class="poster-band poster-band-top"></div>
                <div class="poster-band poster-band-bottom"></div>
                <div class="poster-mark poster-mark-one"></div>
                <div class="poster-mark poster-mark-two"></div>

                <section class="certificate-title">
                  <h2>Teşekkür Belgesi</h2>
                </section>

                <section class="recipient-block">
                  <span>Sayın</span>
                  <h3>{{ normalizedRecipient }}</h3>
                  <p>{{ normalizedReason }}</p>
                </section>

                <section class="message-card">
                  <p :class="['message-text', messageLengthClass]">{{ normalizedMessage }}</p>
                </section>

                <section class="signature-row">
                  <div>
                    <small>Tarih</small>
                    <strong>{{ normalizedDate }}</strong>
                  </div>
                  <div>
                    <small>Takdim Eden</small>
                    <strong>{{ normalizedSigner }}</strong>
                  </div>
                </section>

                <footer class="poster-footer">
                  <img src="/img/logo.jpg" alt="Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği Logosu">
                  <div>
                    <span>Başkent Ağrılılar Eğitim Kültür Sosyal Yardımlaşma ve Dayanışma Derneği</span>
                    <strong>Kütük No: 06-158-130</strong>
                  </div>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import html2canvas from 'html2canvas'

const captureArea = ref(null)
const posterFrame = ref(null)
const posterScale = ref('0.66')
const recipient = ref('')
const reason = ref('Derneğimize yapmış olduğu kıymetli bağış ve destek')
const message = ref('Toplumsal dayanışmamıza güç katan değerli katkılarınız için teşekkür ederiz. Ankara’da yaşayan Ağrılı hemşehrilerimizin birlik, yardımlaşma ve kardeşlik bağlarının güçlenmesine sunduğunuz destek bizim için son derece kıymetlidir.')
const date = ref(new Date().toLocaleDateString('tr-TR'))
const signer = ref('Yönetim Kurulu')

const normalizedRecipient = computed(() => recipient.value.trim() || 'Ad Soyad')
const normalizedReason = computed(() => reason.value.trim() || 'Derneğimize sunmuş olduğu değerli katkılar')
const normalizedMessage = computed(() => message.value.trim() || 'Derneğimizin çalışmalarına sunduğunuz değerli katkılar için teşekkür ederiz. Dayanışma ruhumuzu güçlendiren desteğiniz, hemşehrilerimiz arasında birlik ve beraberlik duygusunu büyütmektedir.')
const normalizedDate = computed(() => date.value.trim() || new Date().toLocaleDateString('tr-TR'))
const normalizedSigner = computed(() => signer.value.trim() || 'Yönetim Kurulu')
const messageLengthClass = computed(() => {
  const length = normalizedMessage.value.length
  if (length > 430) return 'very-long'
  if (length > 300) return 'long'
  if (length > 190) return 'medium'
  return 'short'
})

let resizeObserver

const updatePosterScale = () => {
  if (!posterFrame.value) return
  const width = posterFrame.value.getBoundingClientRect().width
  posterScale.value = String(Math.min(width / 1080, 1))
}

onMounted(() => {
  updatePosterScale()
  resizeObserver = new ResizeObserver(updatePosterScale)
  if (posterFrame.value) resizeObserver.observe(posterFrame.value)
})

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect()
})

const downloadAsPng = async () => {
  if (!captureArea.value) return

  const previousTransform = captureArea.value.style.transform
  captureArea.value.style.transform = 'none'

  let canvas
  try {
    canvas = await html2canvas(captureArea.value, {
      backgroundColor: '#fffdf8',
      scale: 1,
      useCORS: true,
      width: 1080,
      height: 1350,
      windowWidth: 1080,
      windowHeight: 1350
    })
  } finally {
    captureArea.value.style.transform = previousTransform
  }

  const link = document.createElement('a')
  const today = new Date().toISOString().slice(0, 10)
  link.download = `baskent-agrililar-tesekkur-belgesi-${today}.png`
  link.href = canvas.toDataURL('image/png')
  link.click()
}
</script>

<style scoped>
.thanks-tool {
  background: #f5f7fb;
  min-height: 100vh;
}

.thanks-editor {
  padding: 52px 0 88px;
}

.tool-container {
  max-width: 1760px;
  padding-left: 28px;
  padding-right: 28px;
}

.tool-heading {
  max-width: 940px;
  margin-bottom: 28px;
}

.tool-heading span {
  color: var(--primary);
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
}

.tool-heading h1 {
  font-size: clamp(32px, 5vw, 54px);
  font-weight: 900;
  letter-spacing: 0;
  margin: 8px 0 10px;
}

.tool-heading p {
  color: #666565;
  font-size: 17px;
  margin: 0;
}

.tool-layout {
  display: grid;
  grid-template-columns: minmax(340px, 460px) minmax(720px, 1fr);
  gap: 28px;
  align-items: start;
}

.tool-form,
.preview-shell {
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .08);
  border-radius: 8px;
  box-shadow: 0 18px 45px rgba(15, 23, 43, .08);
}

.tool-form {
  display: grid;
  gap: 18px;
  padding: 22px;
}

.tool-form label {
  color: var(--dark);
  font-weight: 800;
}

.tool-form .form-control {
  border-radius: 6px;
  min-height: 48px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.tool-actions {
  display: grid;
  gap: 10px;
}

.tool-actions .btn {
  min-height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.preview-shell {
  padding: 18px;
  overflow: auto;
}

.preview-note {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #555;
  background: #f5f7fb;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  padding: 8px 12px;
  margin-bottom: 14px;
}

.preview-note i {
  color: var(--primary);
}

.poster-scale {
  width: min(100%, 720px);
  aspect-ratio: 4 / 5;
  margin: 0 auto;
}

.thanks-poster {
  position: relative;
  width: 1080px;
  height: 1350px;
  overflow: hidden;
  color: var(--dark);
  background:
    linear-gradient(180deg, #fffdf8 0%, #fff 48%, #fffaf1 100%);
  transform: scale(var(--poster-scale));
  transform-origin: top left;
}

.poster-band,
.poster-mark {
  position: absolute;
  pointer-events: none;
}

.poster-band {
  left: 0;
  right: 0;
  height: 14px;
  background: linear-gradient(90deg, var(--primary) 0 32%, var(--dark) 32% 100%);
}

.poster-band-top {
  top: 0;
}

.poster-band-bottom {
  bottom: 0;
  background: linear-gradient(90deg, var(--dark) 0 66%, var(--primary) 66% 100%);
}

.poster-mark-one {
  width: 620px;
  height: 620px;
  top: -260px;
  right: -220px;
  border: 82px solid rgba(254, 161, 22, .1);
  border-radius: 50%;
}

.poster-mark-two {
  width: 520px;
  height: 520px;
  left: -260px;
  bottom: 160px;
  background: radial-gradient(circle, rgba(15, 23, 43, .055), rgba(15, 23, 43, 0) 68%);
}

.certificate-title,
.recipient-block,
.message-card,
.signature-row,
.poster-footer {
  position: relative;
  z-index: 2;
}

.certificate-title {
  padding: 122px 84px 0;
  text-align: center;
}

.certificate-title h2 {
  color: var(--dark);
  font-size: 82px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1;
  margin: 30px 0 0;
}

.certificate-title h2::after {
  content: "";
  display: block;
  width: 178px;
  height: 8px;
  background: var(--primary);
  margin: 28px auto 0;
  box-shadow: 46px 0 0 var(--dark);
}

.recipient-block {
  width: auto;
  margin: 62px 84px 0;
  text-align: center;
}

.recipient-block span {
  color: #6b7280;
  font-size: 23px;
  font-weight: 800;
}

.recipient-block h3 {
  color: var(--dark);
  font-size: 62px;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.08;
  margin: 12px 0 14px;
}

.recipient-block p {
  color: var(--primary);
  font-size: 26px;
  font-weight: 900;
  line-height: 1.25;
  margin: 0;
}

.message-card {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 48px 84px 0;
  background: #fff;
  border: 2px solid rgba(15, 23, 43, .08);
  border-top: 12px solid var(--primary);
  box-shadow: 0 24px 58px rgba(15, 23, 43, .08);
  padding: 44px 54px;
}

.message-text {
  color: #303441;
  font-size: 34px;
  font-weight: 650;
  line-height: 1.44;
  text-align: center;
  white-space: pre-line;
  margin: 0;
}

.message-text.short {
  font-size: 38px;
  line-height: 1.35;
}

.message-text.medium {
  font-size: 32px;
}

.message-text.long {
  font-size: 27px;
  line-height: 1.36;
}

.message-text.very-long {
  font-size: 23px;
  line-height: 1.3;
}

.signature-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin: 42px 84px 0;
}

.signature-row div {
  min-height: 98px;
  display: grid;
  grid-template-rows: 22px 1fr;
  gap: 8px;
  background: #fff;
  border: 1px solid rgba(15, 23, 43, .1);
  border-bottom: 5px solid var(--primary);
  padding: 16px 18px 18px;
  text-align: center;
}

.signature-row small {
  color: #6b7280;
  font-size: 15px;
  font-weight: 900;
  text-transform: uppercase;
}

.signature-row strong {
  color: var(--dark);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 900;
  line-height: 1.2;
}

.poster-footer {
  position: absolute;
  left: 84px;
  right: 84px;
  bottom: 44px;
  display: flex;
  align-items: center;
  gap: 18px;
  color: #666565;
  border-top: 1px solid rgba(15, 23, 43, .12);
  padding-top: 20px;
  font-size: 17px;
}

.poster-footer img {
  width: 62px;
  height: 62px;
  flex: 0 0 62px;
  border-radius: 50%;
  object-fit: cover;
}

.poster-footer div {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.poster-footer span {
  color: #666565;
  line-height: 1.35;
}

.poster-footer strong {
  color: var(--dark);
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .thanks-editor {
    padding: 36px 0 96px;
  }

  .tool-layout,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .tool-container {
    padding-left: 12px;
    padding-right: 12px;
  }

  .preview-shell {
    padding: 12px;
  }
}
</style>
